import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RichTextActiveFormats,
  RichTextEditorHandle,
  RichTextFormatApi,
  RichTextVendorEditor,
} from './rich-text.type';

const EDITOR_EVENTS = ['selection-change', 'text-change'] as const;

/**
 * Drives one toolbar over any number of editors: whichever registers last is
 * the one the toolbar formats. A single editor is the degenerate case, so this
 * covers both the one-editor and many-editor layouts.
 */
export const useRichTextToolbar = () => {
  const [activeEditor, setActiveEditor] = useState<RichTextFormatApi | null>(
    null,
  );
  const [isActive, setIsActive] = useState(false);
  const [activeFormats, setActiveFormats] = useState<RichTextActiveFormats>({});

  /**
   * Points at the active editor's underlying instance for the colour picker and
   * tooltip helpers, which need vendor internals. A ref rather than state so
   * those helpers read the current editor at call time.
   */
  const vendorEditorRef = useRef<RichTextVendorEditor>(null);

  const refreshActiveFormats = useCallback(
    (editor: RichTextFormatApi | null) => {
      setActiveFormats(editor ? editor.getFormat() : {});
    },
    [],
  );

  const registerEditor = useCallback(
    (handle: RichTextEditorHandle | null) => {
      const editor = handle?.getFormatApi() ?? null;
      vendorEditorRef.current = handle?.getVendorEditor() ?? null;
      setActiveEditor(editor);
      setIsActive(!!editor);
      refreshActiveFormats(editor);
    },
    [refreshActiveFormats],
  );

  const unregisterEditor = useCallback(() => {
    setIsActive(false);
  }, []);

  const applyFormat = useCallback(
    (format: string, value?: unknown) => {
      if (!activeEditor) return;

      const nextValue = value ?? !activeEditor.getFormat()[format];
      activeEditor.format(format, nextValue);
      refreshActiveFormats(activeEditor);
    },
    [activeEditor, refreshActiveFormats],
  );

  useEffect(() => {
    if (!activeEditor) return;

    const handleChange = () => refreshActiveFormats(activeEditor);
    EDITOR_EVENTS.forEach((event) => activeEditor.on(event, handleChange));

    return () => {
      EDITOR_EVENTS.forEach((event) => activeEditor.off(event, handleChange));
    };
  }, [activeEditor, refreshActiveFormats]);

  return {
    registerEditor,
    unregisterEditor,
    applyFormat,
    isActive,
    activeFormats,
    vendorEditorRef,
  };
};

export type RichTextToolbarController = ReturnType<typeof useRichTextToolbar>;
